import type { InputNumberProps } from 'antd';
import { InputNumber } from 'antd';
import classNames from 'classnames';
import React from 'react';
import { useIntl } from 'react-intl';

import { css } from 'config/emotionCss';
import colors from 'config/theme/colors';
import useNumberInputFormat from 'hooks/useNumberInputFormat';

import * as styles from './InputNumber.module.less';

const highlightStyle = css`
  &.highlight,
  &:has(.highlight) {
    background-color: #f5f8ff;
    border: 1px solid ${colors['@primary-color-dark']};
  }
`;

type FBInputNumberProps = {
  className?: string;
  fastType?: boolean;
  placeholder?: string;
  alignRight?: boolean;
  noFormat?: boolean;
} & InputNumberProps;

const FBInputNumber: React.FC<FBInputNumberProps> = ({ className,
  placeholder,
  fastType = true,
  alignRight = true,
  noFormat = false,
  controls = false,
  onFocus,
  parser,
  formatter,
  precision,
  ...rest }) => {
  const intl = useIntl();
  const { formatter: defaultFormatter, parser: defaultParser } = useNumberInputFormat(precision);

  const handleOnFocus = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (event) {
        const value = event?.target?.value ?? '*';
        if (fastType && value.startsWith('0.00')) {
          event.target.select();
        }
      }
      onFocus?.(event);
    },
    [fastType, onFocus]
  );

  return (
    <InputNumber
      placeholder={placeholder && intl.formatMessage({ id: placeholder })}
      className={classNames('w-100', { [styles['input-number-align-right']]: alignRight }, className, highlightStyle)}
      parser={noFormat ? undefined : (parser ?? defaultParser)}
      formatter={noFormat ? undefined : (formatter ?? defaultFormatter)}
      precision={precision}
      {...rest}
      onFocus={handleOnFocus}
      controls={controls}
    />
  );
};

export default FBInputNumber;
